import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User } from "lucide-react";
import { api } from "@/lib/api";

export default function SearchBar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q.trim()) { setResults([]); setActiveIdx(-1); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data.results || []);
        setActiveIdx(data.results?.length ? 0 : -1);
        setOpen(true);
      } catch { /* noop */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    if (!r) return;
    setOpen(false); setQ("");
    if (r.url) navigate(r.url);
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % results.length); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx((i) => (i - 1 + results.length) % results.length); }
    if (e.key === "Enter")     { e.preventDefault(); go(results[activeIdx]); }
    if (e.key === "Escape")    { setOpen(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-2 bg-[#1A1A1A] border border-white/10 px-3 py-2 focus-within:border-amber-500/60 transition-colors">
        <Search size={16} className="text-neutral-500" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => results.length && setOpen(true)}
          placeholder='Search producers — "themartiansmg", "BeatCredsi"…'
          className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-neutral-500"
        />
        <span className="hidden md:inline text-xs text-neutral-500 font-mono-tech">↵</span>
      </div>
      {open && results.length > 0 && (
        <div data-testid="search-results-dropdown"
          className="absolute top-full left-0 right-0 mt-2 bg-[#121212] border border-white/10 z-50 shadow-2xl max-h-80 overflow-auto">
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.id}`}
              type="button"
              data-testid={`search-result-${r.id}`}
              onClick={() => go(r)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-0 flex items-center gap-3 transition-colors ${i === activeIdx ? "bg-amber-500/5" : "hover:bg-white/5"}`}>
              {r.picture ? (
                <img src={r.picture} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" />
              ) : (
                <div className="w-9 h-9 flex items-center justify-center bg-[#1A1A1A] border border-white/10 shrink-0">
                  <User size={14} className="text-neutral-500" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{r.title}</div>
                <div className="text-xs text-neutral-500 truncate font-mono-tech">{r.subtitle}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-lg leading-none text-amber-500">{r.score}</div>
                <div className="text-[13px] uppercase tracking-widest text-neutral-500 font-mono-tech">score</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q.trim().length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#121212] border border-white/10 z-50 px-4 py-3 text-sm text-neutral-500" data-testid="search-no-results">
          No producers found for "{q}".
        </div>
      )}
    </div>
  );
}
